import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Store, Plus, Trash2, ImagePlus, X, Save, Palette, MapPin, Clock, QrCode, Inbox, FileText, DollarSign, Check, Send, Mail, Download, AlertCircle, Users, StickyNote, Search, ShoppingCart, Sparkles } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import PrintableQR from "@/components/PrintableQR";
import BakerLocationSection from "@/components/BakerLocationSection";
import OnboardingWizard from "@/components/OnboardingWizard";
import { THEME_PRESETS, BANNER_STYLES, bannerBackground } from "@/lib/theme";

const money = (c) => `$${(c / 100).toFixed(2)}`;

export default function SellerDashboard() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState({
    business_name: "", tagline: "", city: "", about: "", shop_announcement: "", banner_url: null,
    payment_cashapp: "", payment_venmo: "", payment_paypal: "", payment_zelle: "", payment_notes: "",
    contact_instagram: "", contact_email: "", contact_phone: "",
    theme_accent: "#FF7E67", theme_banner_style: "gradient",
    location_street: "", location_city: "", location_state: "", location_country: "", location_public: false,
    location_open: false, location_note: "", location_hours: {},
  });
  const [products, setProducts] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [showImportMenu, setShowImportMenu] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [onboardingKey, setOnboardingKey] = useState(0);
  const [storefrontEnabled, setStorefrontEnabled] = useState(true);
  const [storefrontSaving, setStorefrontSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { nav("/login"); return; }
    if (!user.is_seller) { nav("/seller/apply"); return; }
    const sp = user.seller_profile || {};
    setProfile({
      business_name: sp.business_name || "",
      tagline: sp.tagline || "",
      city: sp.city || "",
      about: sp.about || "",
      shop_announcement: sp.shop_announcement || "",
      banner_url: sp.banner_url || null,
      payment_cashapp: sp.payment_cashapp || "",
      payment_venmo: sp.payment_venmo || "",
      payment_paypal: sp.payment_paypal || "",
      payment_zelle: sp.payment_zelle || "",
      payment_notes: sp.payment_notes || "",
      contact_instagram: sp.contact_instagram || "",
      contact_email: sp.contact_email || "",
      contact_phone: sp.contact_phone || "",
      theme_accent: sp.theme_accent || "#FF7E67",
      theme_banner_style: sp.theme_banner_style || "gradient",
      location_street: sp.location_street || "",
      location_city: sp.location_city || "",
      location_state: sp.location_state || "",
      location_country: sp.location_country || "",
      location_public: !!sp.location_public,
      location_open: !!sp.location_open,
      location_note: sp.location_note || "",
      location_hours: sp.location_hours || {},
    });
    // storefront_enabled defaults to true unless the vendor explicitly disabled it
    const loc = user.baker_location || {};
    setStorefrontEnabled(loc.storefront_enabled !== false);
    // Auto-open the onboarding wizard the first time (only if not completed AND not dismissed)
    const ob = sp.onboarding || {};
    if (!ob.completed && !ob.dismissed) {
      setShowOnboarding(true);
    }
    loadProducts();
    // eslint-disable-next-line
  }, [user, loading]);

  const toggleStorefrontEnabled = async (val) => {
    setStorefrontEnabled(val);
    setStorefrontSaving(true);
    try {
      await api.patch("/seller/storefront-enabled", { enabled: val });
      toast.success(val ? "Storefront is live in the directory" : "Storefront hidden from directory");
      await refresh();
    } catch (e) {
      setStorefrontEnabled(!val); // revert on error
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setStorefrontSaving(false);
    }
  };

  const loadProducts = async () => {
    try {
      const { data } = await api.get("/seller/me/products");
      setProducts(data);
    } catch (e) { /* ignore */ }
  };

  const saveProfile = async () => {
    if (!profile.contact_email.trim()) {
      toast.error("Public contact email is required so buyers can reach you.");
      return;
    }
    setSavingProfile(true);
    try {
      await api.patch("/seller/me", profile);
      toast.success("Shop updated");
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSavingProfile(false);
  };

  const uploadBanner = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile((p) => ({ ...p, banner_url: data.path }));
      toast.success("Banner uploaded — click Save shop to keep it");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Remove "${p.title}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product removed");
      loadProducts();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!user || user === false || !user.is_seller) return null;

  return (
    <>
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <Store className="w-6 h-6 text-[#FF7E67]" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-[#857163]">Store Fronts</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#3B2C24]">Seller Dashboard</h1>
          </div>
          <button
            onClick={() => setShowOnboarding(true)}
            className="cp-btn-ghost text-sm inline-flex items-center gap-1.5"
            data-testid="open-onboarding-btn"
            title="Storefront setup wizard"
          >
            <Sparkles className="w-3.5 h-3.5" /> Setup wizard
          </button>
          <Link to={`/shop/${user.username}`} className="cp-btn-ghost text-sm">View public shop →</Link>
        </div>

        {/* Storefront on/off — controls directory listing + map pin Shop button */}
        <div className={`cp-card p-4 mb-6 flex items-center gap-3 border-l-4 ${storefrontEnabled ? "border-l-[#5E7A2E]" : "border-l-[#857163]"}`} data-testid="storefront-toggle-card">
          <div className="flex-1">
            <div className="text-sm font-semibold text-[#3B2C24]">Storefront is {storefrontEnabled ? "live" : "hidden"}</div>
            <div className="text-xs text-[#857163] mt-0.5">
              {storefrontEnabled
                ? <>Your storefront appears in the Store Fronts directory and on your map pin (if you have one). Public URL: <Link to={`/shop/${user.username}`} className="text-[#2F80ED] hover:underline">/shop/{user.username}</Link></>
                : <>You're hidden from the Store Fronts directory and the Shop button won't show on your map pin. Products and requests still work if someone has your direct link.</>}
            </div>
          </div>
          <Switch
            checked={storefrontEnabled}
            onCheckedChange={toggleStorefrontEnabled}
            disabled={storefrontSaving}
            data-testid="storefront-enabled-switch"
          />
        </div>

        {user.is_seller && !(user.seller_profile?.contact_email) && (
          <div
            className="cp-card p-4 mb-6 border-l-4 flex items-start gap-3"
            style={{ borderLeftColor: "#FF7E67", background: "rgba(255,126,103,0.06)" }}
            data-testid="missing-contact-email-banner"
          >
            <span className="text-2xl leading-none">✉</span>
            <div className="text-sm text-[#3B2C24]">
              <div className="font-semibold">Add a public contact email</div>
              <div className="text-[#857163] mt-0.5">Buyers (including guests who don't have an account) need a way to reach you. Add your public email below and click <b>Save shop</b>.</div>
            </div>
          </div>
        )}

        <section className="cp-card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl text-[#3B2C24]">Shop Info</h2>
            <button onClick={saveProfile} disabled={savingProfile} className="cp-btn-primary inline-flex items-center gap-1.5 disabled:opacity-70" data-testid="save-shop-btn">
              <Save className="w-4 h-4" /> {savingProfile ? "Saving…" : "Save shop"}
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-medium text-[#3B2C24]">Business name</span>
              <input className="cp-input mt-1" value={profile.business_name} onChange={(e) => setProfile({ ...profile, business_name: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-[#3B2C24]">City</span>
              <input className="cp-input mt-1" value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} placeholder="Boston, MA" />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-sm font-medium text-[#3B2C24]">Tagline</span>
              <input className="cp-input mt-1" value={profile.tagline} onChange={(e) => setProfile({ ...profile, tagline: e.target.value })} maxLength={140} />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-sm font-medium text-[#3B2C24]">About</span>
              <textarea className="cp-input mt-1 min-h-[100px] resize-y" value={profile.about} onChange={(e) => setProfile({ ...profile, about: e.target.value })} maxLength={2000} />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-sm font-medium text-[#3B2C24]">Shop announcement <span className="text-[#857163] font-normal">(280 chars — appears as a banner at the top of your storefront; leave blank to hide)</span></span>
              <input
                className="cp-input mt-1"
                value={profile.shop_announcement || ""}
                onChange={(e) => setProfile({ ...profile, shop_announcement: e.target.value })}
                maxLength={280}
                placeholder="Taking holiday orders now! Book by Dec 15 for guaranteed delivery."
                data-testid="shop-announcement-input"
              />
              <div className="text-[10px] text-[#857163] text-right mt-0.5">{(profile.shop_announcement || "").length}/280</div>
            </label>
            <div className="sm:col-span-2">
              <div className="text-sm font-medium text-[#3B2C24] mb-1.5">Banner image</div>
              {profile.banner_url ? (
                <div className="relative">
                  <img src={fileUrl(profile.banner_url)} alt="" className="w-full h-40 object-cover rounded-2xl border border-[#EBDCD1]" />
                  <button onClick={() => setProfile({ ...profile, banner_url: null })} className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 shadow"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <label className="cp-card p-6 flex items-center justify-center gap-2 cursor-pointer hover:bg-[#F9EFE6]/50 transition" data-testid="banner-upload">
                  <input type="file" accept="image/*" onChange={uploadBanner} className="hidden" />
                  <ImagePlus className="w-5 h-5 text-[#FF7E67]" />
                  <span className="text-sm text-[#857163]">Upload banner (1200x400 recommended)</span>
                </label>
              )}
            </div>
          </div>

          <div className="mt-6 border-t border-[#EBDCD1] pt-4">
            <div className="text-sm font-medium text-[#3B2C24] mb-1 inline-flex items-center gap-1.5"><Palette className="w-4 h-4" /> Storefront theme</div>
            <p className="text-xs text-[#857163] mb-3">Pick a color that reflects your brand. It'll be used for buttons, price highlights, and the banner on your public shop.</p>

            <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-start">
              <div>
                <div className="text-xs text-[#857163] mb-2">Accent color</div>
                <div className="flex flex-wrap gap-2">
                  {THEME_PRESETS.map((preset) => {
                    const active = (profile.theme_accent || "").toLowerCase() === preset.accent.toLowerCase();
                    return (
                      <button
                        key={preset.key}
                        type="button"
                        onClick={() => setProfile({ ...profile, theme_accent: preset.accent })}
                        className="w-10 h-10 rounded-full border-2 transition hover:scale-105"
                        style={{
                          background: preset.accent,
                          borderColor: active ? "#3B2C24" : "#EBDCD1",
                          boxShadow: active ? "0 0 0 2px #fff, 0 0 0 4px #3B2C24" : "none",
                        }}
                        aria-label={preset.name}
                        title={preset.name}
                        data-testid={`theme-preset-${preset.key}`}
                      />
                    );
                  })}
                  <label className="flex items-center gap-1.5 px-2 py-1 rounded-full border border-[#EBDCD1] hover:bg-[#F9EFE6]/50 cursor-pointer">
                    <input
                      type="color"
                      value={profile.theme_accent || "#FF7E67"}
                      onChange={(e) => setProfile({ ...profile, theme_accent: e.target.value })}
                      className="w-6 h-6 border-0 p-0 cursor-pointer bg-transparent"
                      data-testid="theme-custom-color"
                    />
                    <span className="text-xs text-[#857163]">Custom</span>
                  </label>
                </div>
              </div>

              <div className="min-w-[200px]">
                <div className="text-xs text-[#857163] mb-2">Banner style</div>
                <div className="flex gap-1.5 flex-wrap">
                  {BANNER_STYLES.map((bs) => (
                    <button
                      key={bs.key}
                      type="button"
                      onClick={() => setProfile({ ...profile, theme_banner_style: bs.key })}
                      disabled={bs.key === "photo" && !profile.banner_url}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                        profile.theme_banner_style === bs.key
                          ? "bg-[#3B2C24] text-white border-[#3B2C24]"
                          : "bg-white border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
                      }`}
                      data-testid={`banner-style-${bs.key}`}
                    >
                      {bs.label}
                    </button>
                  ))}
                </div>
                {profile.theme_banner_style === "photo" && !profile.banner_url && (
                  <div className="text-[11px] text-[#857163] mt-1">Upload a banner image above to use photo style.</div>
                )}
              </div>
            </div>

            <div className="mt-4">
              <div className="text-xs text-[#857163] mb-2">Live preview</div>
              <div
                className="h-24 rounded-2xl border border-[#EBDCD1]"
                style={{
                  background: bannerBackground(
                    { theme_accent: profile.theme_accent, theme_banner_style: profile.theme_banner_style, banner_url: profile.banner_url },
                    profile.banner_url ? fileUrl(profile.banner_url) : null,
                  ),
                }}
                data-testid="theme-preview-banner"
              />
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-full text-white text-sm font-semibold"
                  style={{ background: profile.theme_accent || "#FF7E67" }}
                  data-testid="theme-preview-btn"
                >
                  Message seller
                </button>
                <span className="text-xs text-[#857163]">← this is how your call-to-action buttons will look</span>
              </div>
            </div>
          </div>

          <div className="mt-6 border-t border-[#EBDCD1] pt-4">
            <div className="text-sm font-medium text-[#3B2C24] mb-2">Payment methods (shown on your listings)</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <label>
                <span className="text-xs text-[#857163]">Cash App $tag</span>
                <input className="cp-input mt-1" placeholder="$sweetstart" value={profile.payment_cashapp} onChange={(e) => setProfile({ ...profile, payment_cashapp: e.target.value })} data-testid="pm-cashapp" />
              </label>
              <label>
                <span className="text-xs text-[#857163]">Venmo @handle</span>
                <input className="cp-input mt-1" placeholder="@sweetstart-bakes" value={profile.payment_venmo} onChange={(e) => setProfile({ ...profile, payment_venmo: e.target.value })} data-testid="pm-venmo" />
              </label>
              <label>
                <span className="text-xs text-[#857163]">PayPal.me handle</span>
                <input className="cp-input mt-1" placeholder="sweetstartbakes" value={profile.payment_paypal} onChange={(e) => setProfile({ ...profile, payment_paypal: e.target.value })} data-testid="pm-paypal" />
              </label>
              <label>
                <span className="text-xs text-[#857163]">Zelle email or phone</span>
                <input className="cp-input mt-1" placeholder="orders@yourbakery.com" value={profile.payment_zelle} onChange={(e) => setProfile({ ...profile, payment_zelle: e.target.value })} data-testid="pm-zelle" />
              </label>
              <label className="sm:col-span-2">
                <span className="text-xs text-[#857163]">Payment notes (optional)</span>
                <input className="cp-input mt-1" placeholder="e.g. 50% deposit required for custom orders" value={profile.payment_notes} onChange={(e) => setProfile({ ...profile, payment_notes: e.target.value })} data-testid="pm-notes" />
              </label>
            </div>
            <div className="mt-3 flex justify-end">
              <button
                onClick={saveProfile}
                disabled={savingProfile}
                className="cp-btn-primary inline-flex items-center gap-1.5 disabled:opacity-70 text-sm px-4 py-2"
                data-testid="save-payment-methods-btn"
              >
                <Save className="w-4 h-4" /> {savingProfile ? "Saving…" : "Save payment methods"}
              </button>
            </div>
          </div>

          <div className="mt-6 border-t border-[#EBDCD1] pt-4">
            <div className="text-sm font-medium text-[#3B2C24] mb-1">Guest-friendly contact <span className="text-[#FF7E67]">*</span></div>
            <p className="text-xs text-[#857163] mb-3">These show up on your product pages so shoppers who don't have a Cake Pop Talk account can still reach you. <b>Public email is required.</b></p>
            <div className="grid sm:grid-cols-2 gap-3">
              <label>
                <span className="text-xs text-[#857163]">Instagram @handle</span>
                <input className="cp-input mt-1" placeholder="@sweetstart.bakes" value={profile.contact_instagram} onChange={(e) => setProfile({ ...profile, contact_instagram: e.target.value })} data-testid="ct-instagram" />
              </label>
              <label>
                <span className="text-xs text-[#857163]">Public email <span className="text-[#FF7E67]">*</span></span>
                <input type="email" required className="cp-input mt-1" placeholder="orders@yourbakery.com" value={profile.contact_email} onChange={(e) => setProfile({ ...profile, contact_email: e.target.value })} data-testid="ct-email" />
              </label>
              <label className="sm:col-span-2">
                <span className="text-xs text-[#857163]">Phone / SMS (optional)</span>
                <input className="cp-input mt-1" placeholder="+1 555 123 4567" value={profile.contact_phone} onChange={(e) => setProfile({ ...profile, contact_phone: e.target.value })} data-testid="ct-phone" />
              </label>
            </div>
          </div>
        </section>

        <section className="cp-card p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="font-heading text-xl text-[#3B2C24]">Products ({products.length})</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setShowImportMenu(true)}
                className="text-sm px-3 py-1.5 rounded-full bg-white border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6] inline-flex items-center gap-1.5"
                data-testid="import-menu-btn"
                title="Import items from your Cake Pop Finder menu"
              >
                <Download className="w-4 h-4" /> Import from menu
              </button>
              <button onClick={() => { setEditing(null); setShowEditor(true); }} className="cp-btn-primary inline-flex items-center gap-1.5" data-testid="add-product-btn">
                <Plus className="w-4 h-4" /> Add product
              </button>
            </div>
          </div>

          {products.length === 0 ? (
            <div className="p-8 text-center text-[#857163]">No products yet. Click "Add product" to list your first one!</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {products.map((p) => (
                <div key={p.id} className={`border border-[#EBDCD1] rounded-2xl p-4 ${!p.is_active ? "opacity-60" : ""}`} data-testid={`product-row-${p.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-16 h-16 rounded-xl bg-[#F9EFE6] overflow-hidden shrink-0">
                      {p.images?.[0] && <img src={fileUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#3B2C24] truncate flex items-center gap-1.5">
                        {p.title}
                        {p.sold_out && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#C2185B] text-white">Sold out</span>}
                        {p.needs_details && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#FFD07F] text-[#B26A00] inline-flex items-center gap-0.5" data-testid={`needs-details-${p.id}`}><AlertCircle className="w-2.5 h-2.5" /> Needs details</span>}
                      </div>
                      <div className="text-sm font-medium">
                        {p.is_custom_order && p.price_cents === 0 ? (
                          <span className="text-[#FF7E67]">Custom</span>
                        ) : p.sale_price_cents != null && p.sale_price_cents !== p.price_cents ? (
                          <>
                            <span className="text-[#C2185B]">{money(p.sale_price_cents)}</span>
                            <span className="text-[#857163] line-through ml-1.5 text-xs">{money(p.price_cents)}</span>
                            <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#FFD07F] text-[#B26A00]">
                              {p.discount_pct ? `${p.discount_pct}% off` : `${money(p.discount_cents)} off`}
                            </span>
                          </>
                        ) : (
                          <span className="text-[#FF7E67]">{money(p.price_cents)}</span>
                        )}
                      </div>
                      <div className="text-xs text-[#857163] truncate">{p.description}</div>
                      {p.available_quantity != null && (
                        <div className="text-[10px] font-semibold text-[#5E7A2E] mt-0.5" data-testid={`product-qty-${p.id}`}>
                          {p.available_quantity > 0 ? `${p.available_quantity} in stock` : "Out of stock"}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <button onClick={() => { setEditing(p); setShowEditor(true); }} className="text-xs px-2 py-1 rounded-full bg-white border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]" data-testid={`edit-product-${p.id}`}>Edit</button>
                      <button onClick={() => deleteProduct(p)} className="text-xs px-2 py-1 rounded-full bg-red-50 text-red-500 hover:bg-red-100" data-testid={`delete-product-${p.id}`}><Trash2 className="w-3 h-3 inline" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Orders & Requests — Phase C + D + E unified */}
        <OrdersSection accent={profile.theme_accent || "#FF7E67"} />

        {/* Cart Orders — checkout flow orders (pay-outside-app) */}
        <CartOrdersSection accent={profile.theme_accent || "#FF7E67"} />

        {/* Customer CRM */}
        <CustomersSection accent={profile.theme_accent || "#FF7E67"} />

        <section className="mt-6" data-testid="seller-location-section">
          <BakerLocationSection
            mode="seller"
            username={user?.username}
            sellerBusinessName={profile.business_name}
            sellerAccent={profile.theme_accent}
          />
        </section>

        {showEditor && (
          <ProductEditor
            initial={editing}
            onClose={() => setShowEditor(false)}
            onSaved={() => { setShowEditor(false); loadProducts(); }}
          />
        )}
        {showImportMenu && (
          <ImportMenuModal
            onClose={() => setShowImportMenu(false)}
            onDone={() => { setShowImportMenu(false); loadProducts(); }}
          />
        )}
      </main>

      <PrintableQR
        open={showQR}
        onClose={() => setShowQR(false)}
        businessName={profile.business_name || user?.username}
        username={user?.username}
        note={profile.location_note}
        hours={profile.location_hours}
        accent={profile.theme_accent}
      />

      {showOnboarding && !showEditor && (
        <OnboardingWizard
          key={onboardingKey}
          initialStep={onboardingStep}
          accent={profile.theme_accent || "#FF7E67"}
          onClose={() => setShowOnboarding(false)}
          onProductAddClick={() => {
            // Advance the wizard to step 5 (Go live) so returning after saving lands them there
            setOnboardingStep(4);
            setOnboardingKey((k) => k + 1);
            setEditing(null);
            setShowEditor(true);
          }}
        />
      )}
    </>
  );
}

function ProductEditor({ initial, onClose, onSaved }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [priceDollars, setPriceDollars] = useState(initial ? (initial.price_cents / 100).toFixed(2) : "");
  const [category, setCategory] = useState(initial?.category || "cake-pops");
  const [images, setImages] = useState(initial?.images || []);
  const [fulfillmentPickup, setFulfillmentPickup] = useState(initial?.fulfillment_pickup ?? true);
  const [fulfillmentShipping, setFulfillmentShipping] = useState(initial?.fulfillment_shipping ?? false);
  const [isCustom, setIsCustom] = useState(initial?.is_custom_order ?? false);
  const [allergens, setAllergens] = useState(initial?.allergens || "");
  const [leadTime, setLeadTime] = useState(initial?.lead_time_days ?? "");
  // Phase B
  const [discountKind, setDiscountKind] = useState(initial?.discount_pct ? "pct" : (initial?.discount_cents ? "cents" : "none"));
  const [discountValue, setDiscountValue] = useState(() => {
    if (initial?.discount_pct) return String(initial.discount_pct);
    if (initial?.discount_cents) return (initial.discount_cents / 100).toFixed(2);
    return "";
  });
  const [soldOut, setSoldOut] = useState(!!initial?.sold_out);
  const [availableQty, setAvailableQty] = useState(initial?.available_quantity ?? "");
  const [lowStockThreshold, setLowStockThreshold] = useState(initial?.low_stock_threshold ?? "");
  const [saving, setSaving] = useState(false);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImages((imgs) => [...imgs, data.path].slice(0, 6));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const save = async () => {
    if (title.trim().length < 3) { toast.error("Title too short"); return; }
    if (description.trim().length < 10) { toast.error("Description too short"); return; }
    if (!fulfillmentPickup && !fulfillmentShipping) { toast.error("Pick at least one fulfillment option"); return; }
    setSaving(true);
    try {
      const payload = {
        title,
        description,
        price_cents: Math.round((parseFloat(priceDollars) || 0) * 100),
        category,
        images,
        fulfillment_pickup: fulfillmentPickup,
        fulfillment_shipping: fulfillmentShipping,
        is_custom_order: isCustom,
        allergens: allergens || null,
        lead_time_days: leadTime === "" ? null : parseInt(leadTime, 10),
        discount_pct: discountKind === "pct" && discountValue !== "" ? Math.min(90, Math.max(0, parseInt(discountValue, 10) || 0)) : null,
        discount_cents: discountKind === "cents" && discountValue !== "" ? Math.round((parseFloat(discountValue) || 0) * 100) : null,
        sold_out: soldOut,
        available_quantity: availableQty === "" || availableQty === null ? null : Math.max(0, parseInt(availableQty, 10) || 0),
        low_stock_threshold: lowStockThreshold === "" || lowStockThreshold === null ? null : Math.max(0, parseInt(lowStockThreshold, 10) || 0),
      };
      if (initial) {
        await api.patch(`/products/${initial.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product listed");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-[#3B2C24]/50 backdrop-blur-sm z-50 grid place-items-center p-4" data-testid="product-editor">
      <div className="cp-card w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl text-[#3B2C24]">{initial ? "Edit product" : "Add product"}</h2>
          <button onClick={onClose} className="text-[#857163] hover:text-[#3B2C24]"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-[#3B2C24]">Title</span>
            <input className="cp-input mt-1" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="product-title" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium text-[#3B2C24]">Price ($)</span>
              <input type="number" step="0.01" min="0" className="cp-input mt-1" value={priceDollars} onChange={(e) => setPriceDollars(e.target.value)} placeholder={isCustom ? "0.00 for quote-only" : "24.00"} data-testid="product-price" />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-[#3B2C24]">Category</span>
              <input className="cp-input mt-1" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="cake-pops" data-testid="product-category" />
            </label>
          </div>
          <label className="block">
            <span className="text-sm font-medium text-[#3B2C24]">Description</span>
            <textarea className="cp-input mt-1 min-h-[120px] resize-y" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="product-description" />
          </label>

          <div>
            <div className="text-sm font-medium text-[#3B2C24] mb-2">Photos (up to 6)</div>
            <div className="flex flex-wrap gap-2">
              {images.map((img, i) => (
                <div key={i} className="relative">
                  <img src={fileUrl(img)} alt="" className="w-20 h-20 rounded-xl object-cover border border-[#EBDCD1]" />
                  <button onClick={() => setImages((imgs) => imgs.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 bg-white rounded-full p-0.5 border border-[#EBDCD1]"><X className="w-3 h-3" /></button>
                </div>
              ))}
              {images.length < 6 && (
                <label className="w-20 h-20 rounded-xl border-2 border-dashed border-[#EBDCD1] grid place-items-center cursor-pointer hover:bg-[#F9EFE6]" data-testid="product-image-upload">
                  <input type="file" accept="image/*" onChange={uploadImage} className="hidden" />
                  <ImagePlus className="w-5 h-5 text-[#FF7E67]" />
                </label>
              )}
            </div>
          </div>

          <div className="flex gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-[#3B2C24]">
              <input type="checkbox" checked={fulfillmentPickup} onChange={(e) => setFulfillmentPickup(e.target.checked)} className="accent-[#FF7E67]" data-testid="fulfillment-pickup" />
              Local pickup
            </label>
            <label className="flex items-center gap-2 text-sm text-[#3B2C24]">
              <input type="checkbox" checked={fulfillmentShipping} onChange={(e) => setFulfillmentShipping(e.target.checked)} className="accent-[#FF7E67]" data-testid="fulfillment-shipping" />
              Shipping
            </label>
            <label className="flex items-center gap-2 text-sm text-[#3B2C24]">
              <input type="checkbox" checked={isCustom} onChange={(e) => setIsCustom(e.target.checked)} className="accent-[#FF7E67]" data-testid="is-custom" />
              Custom orders welcome
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label>
              <span className="text-sm font-medium text-[#3B2C24]">Allergens (optional)</span>
              <input className="cp-input mt-1" value={allergens} onChange={(e) => setAllergens(e.target.value)} placeholder="Contains dairy, may contain nuts" data-testid="product-allergens" />
            </label>
            <label>
              <span className="text-sm font-medium text-[#3B2C24]">Lead time (days)</span>
              <input type="number" min="0" className="cp-input mt-1" value={leadTime} onChange={(e) => setLeadTime(e.target.value)} placeholder="3" data-testid="product-lead-time" />
            </label>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-[#3B2C24]">Available quantity <span className="text-[#B79885] font-normal">(leave blank for unlimited)</span></span>
            <input type="number" min="0" className="cp-input mt-1" value={availableQty} onChange={(e) => setAvailableQty(e.target.value)} placeholder="e.g. 24" data-testid="product-available-quantity" />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-[#3B2C24]">Low stock alert threshold <span className="text-[#B79885] font-normal">(leave blank to disable)</span></span>
            <input type="number" min="0" className="cp-input mt-1" value={lowStockThreshold} onChange={(e) => setLowStockThreshold(e.target.value)} placeholder="e.g. 3" data-testid="product-low-stock-threshold" />
            <span className="block text-[11px] text-[#857163] mt-1">We'll email you + drop a notification when stock hits this number or lower.</span>
          </label>

          {/* Discount + Sold-out */}
          <div className="p-3 rounded-2xl border-2 border-dashed" style={{ borderColor: "#F4A83B55", background: "#FFF9EF" }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-[#3B2C24]">Discount & Availability</span>
              <label className="flex items-center gap-2 text-xs text-[#B26A00] cursor-pointer">
                <input type="checkbox" checked={soldOut} onChange={(e) => setSoldOut(e.target.checked)} className="accent-[#C2185B]" data-testid="product-sold-out" />
                <span className="font-semibold">Mark as sold out</span>
              </label>
            </div>
            <div className="grid grid-cols-[130px_1fr] gap-2 items-end">
              <label>
                <span className="text-[11px] text-[#857163]">Discount type</span>
                <select
                  className="cp-input mt-1"
                  value={discountKind}
                  onChange={(e) => { setDiscountKind(e.target.value); if (e.target.value === "none") setDiscountValue(""); }}
                  data-testid="product-discount-kind"
                >
                  <option value="none">None</option>
                  <option value="pct">% off</option>
                  <option value="cents">$ off</option>
                </select>
              </label>
              <label>
                <span className="text-[11px] text-[#857163]">{discountKind === "cents" ? "Amount off ($)" : discountKind === "pct" ? "Percent off (0–90)" : "—"}</span>
                <input
                  type="number"
                  min="0"
                  max={discountKind === "pct" ? 90 : undefined}
                  step={discountKind === "cents" ? "0.01" : "1"}
                  className="cp-input mt-1 disabled:bg-[#F9EFE6]/60"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  placeholder={discountKind === "cents" ? "5.00" : discountKind === "pct" ? "15" : ""}
                  disabled={discountKind === "none"}
                  data-testid="product-discount-value"
                />
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-[#EBDCD1]">
            <button onClick={onClose} className="cp-btn-ghost">Cancel</button>
            <button onClick={save} disabled={saving} className="cp-btn-primary disabled:opacity-70" data-testid="save-product-btn">
              {saving ? "Saving…" : (initial ? "Save changes" : "Publish product")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// CartOrdersSection — orders placed via the storefront cart (pay-outside-app)
// ============================================================================
function CartOrdersSection({ accent }) {
  const [orders, setOrders] = useState([]);
  const [totals, setTotals] = useState({ pending_total_cents: 0, total_earned_cents: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/seller/orders");
      setOrders(data.orders || []);
      setTotals({ pending_total_cents: data.pending_total_cents || 0, total_earned_cents: data.total_earned_cents || 0 });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/seller/orders/${id}`, { status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const fmtMoney = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

  return (
    <section className="cp-card p-6 mt-6" data-testid="seller-cart-orders-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShoppingCart className="w-5 h-5" style={{ color: accent }} />
          <h2 className="font-heading text-xl text-[#3B2C24]">Cart orders ({orders.length})</h2>
        </div>
        <div className="flex gap-2 text-xs">
          <div className="px-3 py-1.5 rounded-full bg-[#FFF9EF] border border-[#EBDCD1]">
            <span className="text-[#857163]">Pending: </span>
            <b className="text-[#C2185B]" data-testid="pending-total">{fmtMoney(totals.pending_total_cents)}</b>
          </div>
          <div className="px-3 py-1.5 rounded-full bg-[#F9EFE6] border border-[#EBDCD1]">
            <span className="text-[#857163]">Earned: </span>
            <b className="text-[#5E7A2E]" data-testid="earned-total">{fmtMoney(totals.total_earned_cents)}</b>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#857163]">Loading orders…</div>
      ) : orders.length === 0 ? (
        <div className="text-center py-10 text-[#857163] text-sm" data-testid="cart-orders-empty">
          No cart orders yet. When a customer checks out from your storefront, orders appear here.
        </div>
      ) : (
        <div className="space-y-2.5">
          {orders.map((o) => {
            const shortId = o.id.slice(-8).toUpperCase();
            const badge = (
              o.status === "paid" ? { bg: "#5E7A2E", label: "Paid" } :
              o.status === "fulfilled" ? { bg: "#2F80ED", label: "Fulfilled" } :
              o.status === "cancelled" ? { bg: "#857163", label: "Cancelled" } :
              { bg: "#C2185B", label: "Pending payment" }
            );
            return (
              <div key={o.id} className="p-3 rounded-2xl border border-[#EBDCD1] bg-white" data-testid={`cart-order-row-${o.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <div className="font-semibold text-[#3B2C24] flex items-center gap-2 flex-wrap">
                      #{shortId} · {o.customer_name}
                      <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: badge.bg }}>{badge.label}</span>
                    </div>
                    <a href={`mailto:${o.customer_email}`} className="text-xs text-[#2F80ED] hover:underline">{o.customer_email}</a>
                    <div className="text-[11px] text-[#857163] mt-0.5">{fmtDate(o.created_at)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading text-lg" style={{ color: accent }}>{fmtMoney(o.total_cents)}</div>
                    <div className="text-[10px] text-[#857163]">{o.items.reduce((s, it) => s + it.quantity, 0)} items</div>
                  </div>
                </div>
                <div className="mt-2 divide-y divide-[#F1E5D8]">
                  {o.items.map((it) => (
                    <div key={it.product_id} className="py-1.5 flex items-center justify-between text-sm">
                      <span className="text-[#3B2C24] truncate">{it.title} <span className="text-[#857163]">× {it.quantity}</span></span>
                      <span className="text-[#3B2C24] font-medium">{fmtMoney(it.subtotal_cents)}</span>
                    </div>
                  ))}
                </div>
                {o.customer_note && (
                  <div className="mt-2 p-2 rounded-xl bg-[#FFF9EF] border border-[#EBDCD1] text-xs text-[#3B2C24] whitespace-pre-wrap" data-testid={`cart-order-note-${o.id}`}>
                    <span className="text-[10px] uppercase tracking-widest text-[#857163]">Note</span>
                    <div>{o.customer_note}</div>
                  </div>
                )}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {o.status === "pending_payment" && (
                    <>
                      <button onClick={() => setStatus(o.id, "paid")} className="text-[11px] px-2.5 py-1 rounded-full bg-[#5E7A2E] text-white font-semibold" data-testid={`mark-paid-${o.id}`}>Mark paid</button>
                      <button onClick={() => setStatus(o.id, "cancelled")} className="text-[11px] px-2.5 py-1 rounded-full bg-white border border-[#EBDCD1] text-[#857163]" data-testid={`mark-cancelled-${o.id}`}>Cancel</button>
                    </>
                  )}
                  {o.status === "paid" && (
                    <button onClick={() => setStatus(o.id, "fulfilled")} className="text-[11px] px-2.5 py-1 rounded-full bg-[#2F80ED] text-white font-semibold" data-testid={`mark-fulfilled-${o.id}`}>Mark fulfilled</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}


// ============================================================================
// CustomersSection — per-vendor CRM: searchable list + detail modal + manual add
// ============================================================================
function CustomersSection({ accent }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get(`/seller/customers${q ? `?search=${encodeURIComponent(q)}` : ""}`);
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submitSearch = (e) => { e.preventDefault(); load(query.trim()); };
  const fmtMoney = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
  const sourceBadge = (src) => {
    const map = {
      manual: { bg: "#EBDCD1", fg: "#3B2C24", label: "Manual" },
      invoice: { bg: "#F0F6FF", fg: "#2F80ED", label: "Invoice" },
      custom_request: { bg: "#FFF9EF", fg: "#B26A00", label: "Request" },
      order: { bg: "#E7F7EA", fg: "#177E3B", label: "Order" },
    };
    const s = map[src] || map.manual;
    return <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: s.bg, color: s.fg }}>{s.label}</span>;
  };

  return (
    <section className="cp-card p-6 mt-6" data-testid="seller-customers-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5" style={{ color: accent }} />
          <h2 className="font-heading text-xl text-[#3B2C24]">Customers ({rows.length})</h2>
        </div>
        <button onClick={() => setShowAdd(true)} className="cp-btn-primary text-sm inline-flex items-center gap-1.5" data-testid="add-customer-btn">
          <Plus className="w-4 h-4" /> Add customer
        </button>
      </div>

      <form onSubmit={submitSearch} className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#857163]" />
        <input
          className="cp-input !pl-9"
          placeholder="Search by name or email…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="customer-search-input"
        />
      </form>

      {loading ? (
        <div className="text-sm text-[#857163]">Loading customers…</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-10 text-[#857163] text-sm" data-testid="customers-empty">
          No customers yet. Anyone who submits a custom request or receives an invoice will land here automatically.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setDetailId(c.id)}
              className="w-full text-left p-3 rounded-2xl border border-[#EBDCD1] bg-white hover:border-[#FF7E67]/40 hover:bg-[#FFF9EF] transition flex items-center gap-3"
              data-testid={`customer-row-${c.id}`}
            >
              <div className="w-10 h-10 rounded-full grid place-items-center text-white font-semibold text-sm shrink-0" style={{ backgroundColor: accent }}>
                {(c.name || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#3B2C24] truncate flex items-center gap-2">
                  {c.name}
                  {sourceBadge(c.source)}
                  {c.notes && <StickyNote className="w-3 h-3 text-[#B26A00]" />}
                </div>
                <div className="text-xs text-[#857163] truncate">{c.email}</div>
                <div className="text-[11px] text-[#857163] mt-0.5">
                  {c.request_count} request{c.request_count === 1 ? "" : "s"} · {c.invoice_count} invoice{c.invoice_count === 1 ? "" : "s"} · <b className="text-[#5E7A2E]">{fmtMoney(c.total_spent_cents)}</b> spent
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] uppercase tracking-wider text-[#857163]">Last active</div>
                <div className="text-xs text-[#3B2C24] font-medium">{fmtDate(c.last_activity_at)}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {detailId && (
        <CustomerDetailModal
          customerId={detailId}
          accent={accent}
          onClose={() => setDetailId(null)}
          onChange={() => load(query.trim())}
        />
      )}
      {showAdd && (
        <AddCustomerModal
          accent={accent}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(query.trim()); }}
        />
      )}
    </section>
  );
}


function AddCustomerModal({ accent, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { toast.error("Name and email are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/seller/customers", {
        name: name.trim(),
        email: email.trim(),
        notes: notes.trim() || null,
      });
      toast.success(data.updated ? "Existing customer updated" : "Customer added");
      onSaved();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="add-customer-modal">
        <div className="flex items-center justify-between mb-3">
          <div className="font-heading text-lg text-[#3B2C24] flex items-center gap-2"><Users className="w-4 h-4" style={{ color: accent }} /> Add customer</div>
          <button onClick={onClose} className="text-[#857163] hover:text-[#3B2C24]"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="text-xs text-[#857163]">Full name</span>
            <input className="cp-input mt-1" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required data-testid="add-customer-name" />
          </label>
          <label className="block">
            <span className="text-xs text-[#857163]">Contact email</span>
            <input type="email" className="cp-input mt-1" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="add-customer-email" />
          </label>
          <label className="block">
            <span className="text-xs text-[#857163]">Notes <span className="text-[#B79885]">(preferences, allergies, favorites…)</span></span>
            <textarea className="cp-input mt-1 min-h-[100px]" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={4000} data-testid="add-customer-notes" />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="text-sm px-4 py-2 rounded-full bg-white border border-[#EBDCD1] text-[#3B2C24]" data-testid="add-customer-cancel">Cancel</button>
            <button type="submit" disabled={saving} className="cp-btn-primary text-sm px-4 py-2 disabled:opacity-60" data-testid="add-customer-submit">{saving ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}


function CustomerDetailModal({ customerId, accent, onClose, onChange }) {
  const [customer, setCustomer] = useState(null);
  const [notes, setNotes] = useState("");
  const [name, setName] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/seller/customers/${customerId}`);
      setCustomer(data);
      setNotes(data.notes || "");
      setName(data.name || "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      onClose();
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/seller/customers/${customerId}`, { name: name.trim() || customer.name, notes });
      toast.success("Saved");
      onChange();
      setCustomer((c) => ({ ...c, notes, name: name.trim() || c.name }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingNotes(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove this customer from your CRM? Their invoices and requests will remain.")) return;
    try {
      await api.delete(`/seller/customers/${customerId}`);
      toast.success("Customer removed");
      onChange();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const fmtMoney = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

  return (
    <div className="fixed inset-0 z-[9999] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="customer-detail-modal">
        <div className="flex items-center justify-between mb-3">
          <div className="font-heading text-lg text-[#3B2C24] flex items-center gap-2"><Users className="w-4 h-4" style={{ color: accent }} /> Customer profile</div>
          <button onClick={onClose} className="text-[#857163] hover:text-[#3B2C24]" data-testid="customer-detail-close"><X className="w-4 h-4" /></button>
        </div>

        {!customer ? (
          <div className="text-sm text-[#857163]">Loading…</div>
        ) : (
          <>
            <div className="flex items-center gap-4 mb-4 flex-wrap">
              <div className="w-14 h-14 rounded-full grid place-items-center text-white font-heading text-xl shrink-0" style={{ backgroundColor: accent }}>
                {(customer.name || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-[200px]">
                <input
                  className="cp-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                  data-testid="customer-detail-name"
                />
                <a href={`mailto:${customer.email}`} className="text-xs text-[#2F80ED] hover:underline mt-1 inline-block" data-testid="customer-detail-email">{customer.email}</a>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="text-center p-3 rounded-2xl bg-[#FFF9EF] border border-[#EBDCD1]">
                <div className="text-[10px] uppercase tracking-widest text-[#857163]">Total spent</div>
                <div className="font-heading text-lg text-[#5E7A2E]" data-testid="customer-detail-spent">{fmtMoney(customer.total_spent_cents)}</div>
              </div>
              <div className="text-center p-3 rounded-2xl bg-[#F0F6FF] border border-[#EBDCD1]">
                <div className="text-[10px] uppercase tracking-widest text-[#857163]">Invoices</div>
                <div className="font-heading text-lg text-[#2F80ED]" data-testid="customer-detail-invoices">{customer.invoice_count}</div>
              </div>
              <div className="text-center p-3 rounded-2xl bg-[#FFF9EF] border border-[#EBDCD1]">
                <div className="text-[10px] uppercase tracking-widest text-[#857163]">Requests</div>
                <div className="font-heading text-lg text-[#B26A00]" data-testid="customer-detail-requests">{customer.request_count}</div>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center gap-1.5 mb-1">
                <StickyNote className="w-4 h-4 text-[#B26A00]" />
                <span className="text-sm font-semibold text-[#3B2C24]">Notes</span>
                <span className="text-[10px] text-[#857163]">preferences · allergies · favorites</span>
              </div>
              <textarea
                className="cp-input min-h-[100px]"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={4000}
                placeholder="Nut allergy. Prefers pink glitter. Repeat bridal shower client."
                data-testid="customer-detail-notes"
              />
              <div className="flex justify-end mt-2">
                <button onClick={saveNotes} disabled={savingNotes} className="cp-btn-primary text-sm px-4 py-1.5 disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="customer-detail-save">
                  <Save className="w-3.5 h-3.5" /> {savingNotes ? "Saving…" : "Save"}
                </button>
              </div>
            </div>

            <div className="mb-4">
              <div className="text-sm font-semibold text-[#3B2C24] mb-1 flex items-center gap-1.5"><Inbox className="w-4 h-4" /> Custom requests ({customer.custom_requests.length})</div>
              {customer.custom_requests.length === 0 ? (
                <div className="text-xs text-[#857163] italic">No requests yet.</div>
              ) : (
                <div className="space-y-1.5">
                  {customer.custom_requests.map((r) => (
                    <div key={r.id} className="p-2.5 rounded-xl border border-[#EBDCD1] bg-white" data-testid={`detail-request-${r.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${r.status === "converted" ? "bg-[#2F80ED] text-white" : r.status === "responded" ? "bg-[#5E7A2E] text-white" : r.status === "closed" ? "bg-[#857163] text-white" : "bg-[#C2185B] text-white"}`}>{r.status}</span>
                        <span className="text-[11px] text-[#857163]">{fmtDate(r.created_at)}</span>
                      </div>
                      <div className="text-xs text-[#3B2C24] whitespace-pre-wrap">{r.description}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-4">
              <div className="text-sm font-semibold text-[#3B2C24] mb-1 flex items-center gap-1.5"><FileText className="w-4 h-4" /> Invoices ({customer.invoices.length})</div>
              {customer.invoices.length === 0 ? (
                <div className="text-xs text-[#857163] italic">No invoices yet.</div>
              ) : (
                <div className="space-y-1.5">
                  {customer.invoices.map((inv) => (
                    <div key={inv.id} className="p-2.5 rounded-xl border border-[#EBDCD1] bg-white flex items-center justify-between" data-testid={`detail-invoice-${inv.id}`}>
                      <div>
                        <div className="text-sm font-semibold text-[#3B2C24]">{inv.number}</div>
                        <div className="text-[11px] text-[#857163]">{fmtDate(inv.created_at)} · Due {inv.due_date || "—"}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-[#3B2C24]">{fmtMoney(inv.total_cents)}</div>
                        <span className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full ${inv.status === "paid" ? "bg-[#5E7A2E] text-white" : "bg-[#C2185B] text-white"}`}>{inv.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-[#EBDCD1]">
              <button onClick={remove} className="text-xs text-[#C2185B] hover:underline inline-flex items-center gap-1" data-testid="customer-detail-remove">
                <Trash2 className="w-3 h-3" /> Remove from CRM
              </button>
              <div className="text-[11px] text-[#857163]">Last active: {fmtDate(customer.last_activity_at)}</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ============================================================================
// ImportMenuModal — pulls items from the vendor's Cake Pop Finder menu into
// the storefront as products. Conflicts (matching image or title) let the
// vendor pick between "Skip" and "Overwrite" per row.
// ============================================================================
function ImportMenuModal({ onClose, onDone }) {
  const [items, setItems] = useState(null); // null = loading, [] = none
  const [decisions, setDecisions] = useState({}); // { menu_index: "import" | "overwrite" | "skip" }
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/seller/products/import-menu/preview");
        setItems(data.items || []);
        const initial = {};
        (data.items || []).forEach((it) => {
          initial[it.menu_index] = it.matched_product_id ? "skip" : "import";
        });
        setDecisions(initial);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
        setItems([]);
      }
    })();
  }, []);

  const setDec = (idx, val) => setDecisions((d) => ({ ...d, [idx]: val }));

  const submit = async () => {
    if (!items || items.length === 0) return;
    const payload = {
      decisions: items.map((it) => ({
        menu_index: it.menu_index,
        action: decisions[it.menu_index] || "skip",
        target_product_id: it.matched_product_id || null,
      })),
    };
    setSaving(true);
    try {
      const { data } = await api.post("/seller/products/import-menu", payload);
      const bits = [];
      if (data.imported) bits.push(`${data.imported} imported`);
      if (data.overwritten) bits.push(`${data.overwritten} overwritten`);
      if (data.skipped) bits.push(`${data.skipped} skipped`);
      toast.success(bits.length ? bits.join(" · ") : "Nothing to import");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toImportCount = items ? items.filter((it) => (decisions[it.menu_index] || "skip") === "import").length : 0;
  const toOverwriteCount = items ? items.filter((it) => (decisions[it.menu_index] || "skip") === "overwrite").length : 0;
  const conflictCount = items ? items.filter((it) => it.matched_product_id).length : 0;

  return (
    <div className="fixed inset-0 z-[9999] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="import-menu-modal">
        <div className="flex items-center justify-between mb-2">
          <div className="font-heading text-lg text-[#3B2C24] flex items-center gap-2">
            <Download className="w-5 h-5 text-[#FF7E67]" /> Import from your Finder menu
          </div>
          <button onClick={onClose} className="text-[#857163] hover:text-[#3B2C24]" data-testid="import-menu-close"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-[#857163] mb-4">
          Bring photos + names from your Cake Pop Finder menu into your storefront. You'll add pricing and details after. If a menu item already exists as a product, choose whether to <b>skip</b> or <b>overwrite</b> that product's image + name.
        </p>

        {items === null && (
          <div className="p-8 text-center text-[#857163] text-sm" data-testid="import-menu-loading">Loading your menu…</div>
        )}

        {items && items.length === 0 && (
          <div className="p-8 text-center text-[#857163] text-sm" data-testid="import-menu-empty">
            No menu items with photos found. Add some to your <b>Finder Presence → Menu</b> section first.
          </div>
        )}

        {items && items.length > 0 && (
          <>
            {conflictCount > 0 && (
              <div className="mb-3 text-xs bg-[#FFF8ED] border border-[#F4A83B]/30 text-[#B26A00] rounded-xl px-3 py-2 flex items-start gap-2" data-testid="import-menu-conflict-banner">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>{conflictCount} of your menu items match an existing product. Pick <b>Skip</b> to leave it alone, or <b>Overwrite</b> to replace the product's image + name (pricing stays).</div>
              </div>
            )}
            <div className="space-y-2">
              {items.map((it) => {
                const decision = decisions[it.menu_index] || "skip";
                const hasConflict = !!it.matched_product_id;
                return (
                  <div key={it.menu_index} className="border border-[#EBDCD1] rounded-2xl p-3 flex items-start gap-3" data-testid={`import-row-${it.menu_index}`}>
                    <div className="w-16 h-16 rounded-xl bg-[#F9EFE6] overflow-hidden shrink-0">
                      <img src={fileUrl(it.image_url)} alt={it.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-[#3B2C24] truncate">{it.title}</div>
                      {hasConflict ? (
                        <div className="text-[11px] text-[#B26A00] mt-0.5">Already exists as "<b>{it.matched_product_title}</b>"</div>
                      ) : (
                        <div className="text-[11px] text-[#857163] mt-0.5">New product</div>
                      )}
                      <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]" data-testid={`import-row-actions-${it.menu_index}`}>
                        {hasConflict ? (
                          <>
                            <button
                              type="button"
                              onClick={() => setDec(it.menu_index, "skip")}
                              className={`px-2.5 py-1 rounded-full border font-semibold ${decision === "skip" ? "bg-[#857163] text-white border-[#857163]" : "bg-white text-[#857163] border-[#EBDCD1]"}`}
                              data-testid={`decision-skip-${it.menu_index}`}
                            >Skip</button>
                            <button
                              type="button"
                              onClick={() => setDec(it.menu_index, "overwrite")}
                              className={`px-2.5 py-1 rounded-full border font-semibold ${decision === "overwrite" ? "bg-[#C2185B] text-white border-[#C2185B]" : "bg-white text-[#C2185B] border-[#C2185B]/30"}`}
                              data-testid={`decision-overwrite-${it.menu_index}`}
                            >Overwrite</button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => setDec(it.menu_index, "import")}
                              className={`px-2.5 py-1 rounded-full border font-semibold ${decision === "import" ? "bg-[#5E7A2E] text-white border-[#5E7A2E]" : "bg-white text-[#5E7A2E] border-[#5E7A2E]/30"}`}
                              data-testid={`decision-import-${it.menu_index}`}
                            >Import</button>
                            <button
                              type="button"
                              onClick={() => setDec(it.menu_index, "skip")}
                              className={`px-2.5 py-1 rounded-full border font-semibold ${decision === "skip" ? "bg-[#857163] text-white border-[#857163]" : "bg-white text-[#857163] border-[#EBDCD1]"}`}
                              data-testid={`decision-skip-${it.menu_index}`}
                            >Skip</button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-[#EBDCD1] pt-4">
              <div className="text-xs text-[#857163]" data-testid="import-menu-summary">
                Ready to import: <b className="text-[#5E7A2E]">{toImportCount}</b>
                {toOverwriteCount > 0 && <> · Overwrite: <b className="text-[#C2185B]">{toOverwriteCount}</b></>}
              </div>
              <div className="flex gap-2">
                <button onClick={onClose} className="text-sm px-4 py-2 rounded-full bg-white border border-[#EBDCD1] text-[#3B2C24]" data-testid="import-menu-cancel">Cancel</button>
                <button
                  onClick={submit}
                  disabled={saving || (toImportCount === 0 && toOverwriteCount === 0)}
                  className="cp-btn-primary text-sm px-4 py-2 disabled:opacity-60"
                  data-testid="import-menu-submit"
                >{saving ? "Importing…" : "Import selected"}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ============================================================================
// OrdersSection — unified Custom Requests + Invoices + earnings totals
// ============================================================================
function OrdersSection({ accent }) {
  const [tab, setTab] = useState("requests");
  const [requests, setRequests] = useState([]);
  const [invoiceData, setInvoiceData] = useState({ invoices: [], total_earned_cents: 0, outstanding_cents: 0 });
  const [showInvForm, setShowInvForm] = useState(false);
  const [invoicePrefill, setInvoicePrefill] = useState(null);

  const loadRequests = async () => {
    try { const { data } = await api.get("/seller/custom-requests"); setRequests(data || []); } catch {}
  };
  const loadInvoices = async () => {
    try { const { data } = await api.get("/seller/invoices"); setInvoiceData(data); } catch {}
  };
  useEffect(() => { loadRequests(); loadInvoices(); }, []);

  const setRequestStatus = async (id, status) => {
    try { await api.patch(`/seller/custom-requests/${id}`, { status }); loadRequests(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleInvoicePaid = async (inv) => {
    const nextStatus = inv.status === "paid" ? "unpaid" : "paid";
    try { await api.patch(`/seller/invoices/${inv.id}`, { status: nextStatus }); loadInvoices(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const resendInvoice = async (id) => {
    try { await api.post(`/seller/invoices/${id}/resend`); toast.success("Emailed again"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const convertRequestToInvoice = (r) => {
    // Extract a numeric price from the free-form budget string (e.g. "$40" or "40-60" -> 40)
    const budgetMatch = String(r.budget || "").match(/(\d+(?:\.\d+)?)/);
    const budgetDollars = budgetMatch ? budgetMatch[1] : "";
    setInvoicePrefill({
      customer_name: r.customer_name || "",
      customer_email: r.customer_email || "",
      description: r.description || "",
      unit_price_dollars: budgetDollars,
      custom_request_id: r.id,
    });
    setShowInvForm(true);
    setTab("invoices");
  };

  const closeInvForm = () => { setShowInvForm(false); setInvoicePrefill(null); };
  const onInvoiceSaved = () => { closeInvForm(); loadInvoices(); loadRequests(); };

  const newRequests = requests.filter((r) => r.status === "new").length;

  return (
    <section className="cp-card p-6 mt-6" data-testid="seller-orders-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-heading text-xl text-[#3B2C24] flex items-center gap-2"><Inbox className="w-5 h-5" style={{ color: accent }} /> Orders & Requests</h2>
        <div className="flex gap-2 text-xs">
          <div className="px-3 py-1 rounded-full bg-[#F1F7E5] text-[#5E7A2E] font-semibold">Earned {money(invoiceData.total_earned_cents)}</div>
          <div className="px-3 py-1 rounded-full bg-[#FFF9EF] text-[#B26A00] font-semibold">Outstanding {money(invoiceData.outstanding_cents)}</div>
        </div>
      </div>
      <div className="flex gap-2 border-b border-[#EBDCD1] mb-4">
        <button onClick={() => setTab("requests")} className={`px-3 py-2 text-sm font-semibold -mb-px border-b-2 ${tab === "requests" ? "border-[#FF7E67] text-[#3B2C24]" : "border-transparent text-[#857163]"}`} data-testid="tab-requests">
          Custom Requests {newRequests > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] rounded-full bg-[#C2185B] text-white px-1">{newRequests}</span>}
        </button>
        <button onClick={() => setTab("invoices")} className={`px-3 py-2 text-sm font-semibold -mb-px border-b-2 ${tab === "invoices" ? "border-[#FF7E67] text-[#3B2C24]" : "border-transparent text-[#857163]"}`} data-testid="tab-invoices">
          Invoices ({invoiceData.invoices.length})
        </button>
      </div>

      {tab === "requests" && (
        <div className="space-y-2" data-testid="requests-list">
          {requests.length === 0 ? (
            <div className="p-6 text-center text-[#857163] text-sm">No custom requests yet. Share your storefront to get some!</div>
          ) : requests.map((r) => (
            <div key={r.id} className={`p-3 rounded-2xl border ${r.status === "new" ? "border-[#FF7E67]/40 bg-[#FFF9EF]" : r.status === "converted" ? "border-[#2F80ED]/30 bg-[#F0F6FF]" : "border-[#EBDCD1] bg-white"}`} data-testid={`request-row-${r.id}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0">
                  <div className="font-semibold text-[#3B2C24] flex items-center gap-2 flex-wrap">
                    {r.customer_name}
                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${r.status === "new" ? "bg-[#C2185B] text-white" : r.status === "responded" ? "bg-[#5E7A2E] text-white" : r.status === "converted" ? "bg-[#2F80ED] text-white" : "bg-[#857163] text-white"}`}>{r.status}</span>
                  </div>
                  <a href={`mailto:${r.customer_email}`} className="text-xs text-[#2F80ED] hover:underline">{r.customer_email}</a>
                </div>
                <div className="text-[11px] text-[#857163] shrink-0">{new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
              </div>
              <div className="text-sm text-[#3B2C24] whitespace-pre-wrap">{r.description}</div>
              {r.reference_image_url && (
                <a href={fileUrl(r.reference_image_url)} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block" data-testid={`request-reference-${r.id}`}>
                  <img src={fileUrl(r.reference_image_url)} alt="reference" className="max-h-40 rounded-xl border border-[#EBDCD1] hover:opacity-90 transition" />
                </a>
              )}
              {(r.budget || r.needed_by) && (
                <div className="mt-1.5 text-xs text-[#857163]">
                  {r.budget && <span>Budget: <b>{r.budget}</b></span>}
                  {r.budget && r.needed_by && " · "}
                  {r.needed_by && <span>Needed: <b>{r.needed_by}</b></span>}
                </div>
              )}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {r.status !== "converted" && (
                  <button onClick={() => convertRequestToInvoice(r)} className="text-[11px] px-2.5 py-1 rounded-full bg-[#2F80ED] text-white font-semibold inline-flex items-center gap-1" data-testid={`convert-to-invoice-${r.id}`}>
                    <FileText className="w-3 h-3" /> Convert to invoice
                  </button>
                )}
                {r.status === "converted" && (
                  <span className="text-[11px] px-2.5 py-1 rounded-full bg-white border border-[#2F80ED]/30 text-[#2F80ED] font-semibold inline-flex items-center gap-1">
                    <Check className="w-3 h-3" /> Invoiced
                  </span>
                )}
                {r.status !== "responded" && r.status !== "converted" && <button onClick={() => setRequestStatus(r.id, "responded")} className="text-[11px] px-2.5 py-1 rounded-full bg-[#5E7A2E] text-white font-semibold" data-testid={`mark-responded-${r.id}`}>Mark responded</button>}
                {r.status !== "closed" && r.status !== "converted" && <button onClick={() => setRequestStatus(r.id, "closed")} className="text-[11px] px-2.5 py-1 rounded-full bg-white border border-[#EBDCD1] text-[#857163]" data-testid={`mark-closed-${r.id}`}>Close</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "invoices" && (
        <div className="space-y-2" data-testid="invoices-list">
          <div className="flex justify-end mb-1">
            <button onClick={() => { setInvoicePrefill(null); setShowInvForm(true); }} className="cp-btn-primary inline-flex items-center gap-1.5 text-sm" data-testid="new-invoice-btn">
              <Plus className="w-4 h-4" /> New invoice
            </button>
          </div>
          {invoiceData.invoices.length === 0 ? (
            <div className="p-6 text-center text-[#857163] text-sm">No invoices yet. Create one to bill a customer.</div>
          ) : invoiceData.invoices.map((inv) => (
            <div key={inv.id} className="p-3 rounded-2xl border border-[#EBDCD1] bg-white" data-testid={`invoice-row-${inv.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 font-semibold text-[#3B2C24]">
                    <FileText className="w-4 h-4 text-[#857163]" /> {inv.invoice_number}
                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${inv.status === "paid" ? "bg-[#5E7A2E] text-white" : "bg-[#FFD07F] text-[#B26A00]"}`}>{inv.status}</span>
                  </div>
                  <div className="text-xs text-[#857163]">{inv.customer_name} · <a className="text-[#2F80ED] hover:underline" href={`mailto:${inv.customer_email}`}>{inv.customer_email}</a>{inv.due_date ? <> · due <b>{inv.due_date}</b></> : null}</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-lg font-bold" style={{ color: accent }}>{money(inv.total_cents)}</div>
                  <button onClick={() => toggleInvoicePaid(inv)} className={`text-[11px] px-2.5 py-1 rounded-full font-semibold ${inv.status === "paid" ? "bg-white border border-[#EBDCD1] text-[#857163]" : "bg-[#5E7A2E] text-white"}`} data-testid={`toggle-paid-${inv.id}`}>
                    <Check className="w-3 h-3 inline mr-1" />{inv.status === "paid" ? "Mark unpaid" : "Mark paid"}
                  </button>
                  <button onClick={() => resendInvoice(inv.id)} className="text-[11px] px-2.5 py-1 rounded-full bg-white border border-[#EBDCD1] text-[#3B2C24]" data-testid={`resend-${inv.id}`}>
                    <Mail className="w-3 h-3 inline mr-1" />Resend
                  </button>
                </div>
              </div>
              <ul className="mt-2 text-xs text-[#857163]">
                {inv.line_items.map((it, idx) => (
                  <li key={idx}>{it.description} × {it.quantity} @ {money(it.unit_price_cents)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {showInvForm && <NewInvoiceModal accent={accent} prefill={invoicePrefill} onClose={closeInvForm} onSaved={onInvoiceSaved} />}
    </section>
  );
}

function NewInvoiceModal({ accent, onClose, onSaved, prefill }) {
  const [customerName, setCustomerName] = useState(prefill?.customer_name || "");
  const [customerEmail, setCustomerEmail] = useState(prefill?.customer_email || "");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [items, setItems] = useState(() => {
    if (prefill?.description) {
      return [{ description: prefill.description.slice(0, 200), quantity: 1, unit_price_dollars: prefill.unit_price_dollars || "" }];
    }
    return [{ description: "", quantity: 1, unit_price_dollars: "" }];
  });
  const [submitting, setSubmitting] = useState(false);

  const addItem = () => setItems([...items, { description: "", quantity: 1, unit_price_dollars: "" }]);
  const updateItem = (idx, patch) => setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const total = items.reduce((s, it) => s + (parseFloat(it.unit_price_dollars || 0) * (parseInt(it.quantity, 10) || 0)), 0);

  const submit = async (e) => {
    e.preventDefault();
    const cleanItems = items
      .filter((it) => it.description && parseInt(it.quantity, 10) > 0)
      .map((it) => ({ description: it.description, quantity: parseInt(it.quantity, 10), unit_price_cents: Math.round(parseFloat(it.unit_price_dollars || 0) * 100) }));
    if (cleanItems.length === 0) { toast.error("Add at least one line item"); return; }
    setSubmitting(true);
    try {
      await api.post("/seller/invoices", {
        customer_name: customerName.trim(),
        customer_email: customerEmail.trim(),
        line_items: cleanItems,
        due_date: dueDate || null,
        notes: notes || null,
        send_email: sendEmail,
        custom_request_id: prefill?.custom_request_id || null,
      });
      toast.success(sendEmail ? "Invoice created & emailed" : "Invoice created");
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-invoice-modal">
        <div className="flex items-center justify-between mb-3">
          <div className="font-heading text-lg text-[#3B2C24] flex items-center gap-2"><FileText className="w-4 h-4" style={{ color: accent }} /> {prefill?.custom_request_id ? "Convert request to invoice" : "New invoice"}</div>
          <button onClick={onClose} className="text-[#857163] hover:text-[#3B2C24]"><X className="w-4 h-4" /></button>
        </div>
        {prefill?.custom_request_id && (
          <div className="mb-3 text-xs bg-[#F0F6FF] border border-[#2F80ED]/20 text-[#2F80ED] rounded-xl px-3 py-2" data-testid="prefill-banner">
            Prefilled from custom request — creating this invoice will mark the request as <b>converted</b>.
          </div>
        )}
        <form onSubmit={submit} className="space-y-2.5">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-xs text-[#857163]">Customer name</span>
              <input className="cp-input mt-1" value={customerName} onChange={(e) => setCustomerName(e.target.value)} required data-testid="inv-name" />
            </label>
            <label className="block">
              <span className="text-xs text-[#857163]">Customer email</span>
              <input type="email" className="cp-input mt-1" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} required data-testid="inv-email" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs text-[#857163]">Due date <span className="text-[#B79885]">(optional)</span></span>
            <input type="date" className="cp-input mt-1" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="inv-due" />
          </label>
          <div>
            <div className="text-xs text-[#857163] mb-1">Line items</div>
            {items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_60px_80px_28px] gap-1.5 mb-1.5" data-testid={`inv-item-${idx}`}>
                <input className="cp-input" placeholder="Item description" value={it.description} onChange={(e) => updateItem(idx, { description: e.target.value })} />
                <input type="number" min="1" className="cp-input text-center" placeholder="Qty" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} />
                <input type="number" min="0" step="0.01" className="cp-input text-right" placeholder="$0.00" value={it.unit_price_dollars} onChange={(e) => updateItem(idx, { unit_price_dollars: e.target.value })} />
                <button type="button" onClick={() => removeItem(idx)} className="text-[#C2185B] hover:opacity-70" title="Remove"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button type="button" onClick={addItem} className="text-xs text-[#2F80ED] hover:underline" data-testid="inv-add-item"><Plus className="w-3 h-3 inline" /> Add another item</button>
          </div>
          <div className="flex justify-between items-center border-t border-[#EBDCD1] pt-2">
            <span className="text-xs text-[#857163] uppercase tracking-wide">Total</span>
            <span className="text-xl font-bold" style={{ color: accent }}>${total.toFixed(2)}</span>
          </div>
          <label className="block">
            <span className="text-xs text-[#857163]">Notes <span className="text-[#B79885]">(optional)</span></span>
            <textarea className="cp-input mt-1 min-h-[60px] resize-y" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={400} data-testid="inv-notes" />
          </label>
          <label className="flex items-center gap-2 text-sm text-[#3B2C24] cursor-pointer">
            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="accent-[#FF7E67]" data-testid="inv-send-email" />
            <Send className="w-3.5 h-3.5" /> Email the invoice to the customer right now
          </label>
          <button type="submit" disabled={submitting} className="w-full py-2.5 rounded-full text-white font-semibold disabled:opacity-60" style={{ background: accent }} data-testid="inv-submit">
            {submitting ? "Saving…" : "Create invoice"}
          </button>
        </form>
      </div>
    </div>
  );
}
